import { createStore } from 'botframework-webchat';
import { DisableReasons, DisableReason } from './hooks';
import {
    logEvent as logAnalyticsEvent,
    analyticsLinkClickHandler,
    getAnalyticsUserId,
} from './analytics';
import { initTealium } from './tealium';
import {
    botMessageIsDropdown,
    clearRehydrationData,
    getAppVersion,
    getBrand,
    getCustomScheme,
    getTenantName,
    getUrlParam,
    ignoreMessageForDisableTextSend,
    isUnified,
    isMobile,
    isMobileBrowser,
    isMychartEnabledUser,
    isRehydratedMessage,
    postChatActivity,
    setRehydrationExpirations,
    setTokenFromGet,
    getTextInputEnabled,
    setTextInputEnabled,
    clearTextInputEnabled,
    isGenesysResume,
    setWatermark,
    setCheckpointWatermark,
    setCurrentWatermark,
    getCurrentWatermark,
    getSessionToken,
    getConversationIdFromToken,
    REHYDRATION_COOKIE_EXP_MINUTES,
    getOriginHost,
    getRecipientName,
    getRecipientId,
} from './helpers';
import { InputMode, CustomTextInput, shouldReconnect } from './hooks';
import {
    logEvent,
    getCorrelationId,
    getConversationId,
    getSessionId,
} from './logging';
import { replaceTenantSpecificData } from './tenantData/index';
import {
    Action,
    Activity,
    AmplitudeEntity,
    CustomFeedbackEntity,
    Dispatch,
    GlobalInformationAlertType,
    GlobalInformationAlertValue,
    InitLoaderValues,
    InputModeEntity,
    Location as GeoLocation,
    Session,
    WatermarkEntity,
} from './types';

import {
    OnLiveChatActiveEvent,
    OnLiveChatClosedEvent,
    OnLiveChatClosedInTabEvent,
    OnLiveChatOpeningEvent,
    getLiveChatService,
} from './liveChatService';
import { fetchPcpLinkedInfoForAnalytics } from './api/getPcpLinkedInfoForAnalytics';
import { Config } from './config';
import { AmpFlagSet } from './amplitude';
import {
    postAnchorClickEventToParentApp,
    postMessageAssistMessage,
} from './messageassist';
import {
    INACTIVE_TIMEOUT_MS,
    MESSAGE_ASSIST_MESSAGE_EVENT_TYPES,
    TENANTS,
} from './constants';
import { handleEventActivity } from './eventActivityHandler';

export let store: any;
let userInfo: any;
let lastActive: number;

let autoResponse: string;
const INPUT_AGE = 'inputAge';
const INPUT_GENDER = 'inputGender';
const liveChatService = getLiveChatService();

const closeBotLinkClickHandler = (_e: any) => {
    parent.window.postMessage('closeChatbot', '*');
};

const onLinkClick = async (e: any) => {
    analyticsLinkClickHandler(e);
    // Only post hyperlink click event to channel if on Prov app
    if (isUnified() && getUrlParam('hostappchannel')) {
        const anchor = e.target;
        await postAnchorClickEventToParentApp(e, anchor.href, anchor.innerText);
    }
};

const addLinkClickEvents = () => {
    const botMessages = Array.from(
        document.querySelectorAll('[data-bot-msg="true"]')
    );
    for (const botMessage of botMessages) {
        const links = Array.from(botMessage.querySelectorAll('a'));
        for (const link of links) {
            link.addEventListener('click', onLinkClick);
            link.addEventListener('click', closeBotLinkClickHandler);
        }
    }
};

const replacePlaceholders = (text: string): string => {
    autoResponse = '';
    let matchIndex = text.indexOf('{{');
    while (matchIndex > -1) {
        const key = text.slice(matchIndex + 2, text.indexOf('}}'));
        let value = '';
        if (key === 'firstName' && userInfo && userInfo.firstName)
            value = userInfo.firstName;
        else if (key === INPUT_AGE && userInfo && userInfo.age)
            autoResponse = INPUT_AGE;
        else if (key === INPUT_GENDER && userInfo && userInfo.gender)
            autoResponse = INPUT_GENDER;
        text = text.replace('{{' + key + '}}', value);
        matchIndex = text.indexOf('{{');
    }
    return text;
};

const selectButton = (targetButton: any): void => {
    targetButton.classList.add('selected');
    targetButton.classList.add('old-button');
};

const sendAutoResponseBasedOnUserInfo = (activity: Activity): void => {
    if (autoResponse === INPUT_AGE) {
        // check whether the input is dropdown or not
        if (
            activity.attachments &&
            activity.attachments.length &&
            botMessageIsDropdown(activity.attachments[0].content as any)
        ) {
            let idx = '';
            if (userInfo.age < 2) idx = '0';
            else if (userInfo.age <= 4) idx = '1';
            else if (userInfo.age <= 9) idx = '2';
            else if (userInfo.age <= 12) idx = '3';
            else if (userInfo.age <= 17) idx = '4';
            else if (userInfo.age <= 29) idx = '5';
            else if (userInfo.age <= 39) idx = '6';
            else if (userInfo.age <= 49) idx = '7';
            else if (userInfo.age <= 59) idx = '8';
            else if (userInfo.age <= 64) idx = '9';
            else if (userInfo.age <= 69) idx = '10';
            else if (userInfo.age <= 79) idx = '11';
            else idx = '12';

            if (!idx) return;

            activity.defaultValue = idx;
            setTimeout(() => {
                const buttons =
                    document.getElementsByClassName('ac-pushButton');
                for (let i = 0; i < buttons.length; i++) {
                    if (
                        buttons[i].getAttribute('aria-label') ===
                            activity.text &&
                        !buttons[i].classList.value.includes('disabled')
                    ) {
                        const selectedElement: HTMLElement = buttons[
                            i
                        ] as HTMLElement;
                        if (selectedElement) selectedElement.click();
                    }
                }
            }, 1000);
        } else {
            setTimeout(() => {
                store.dispatch({
                    type: 'WEB_CHAT/SEND_MESSAGE',
                    payload: {
                        text: userInfo.age,
                        textFormat: 'plain',
                        correlationId: getCorrelationId(),
                    },
                });
            }, 1000);
        }
    } else if (
        autoResponse === INPUT_GENDER &&
        activity.attachments &&
        activity.attachments.length
    ) {
        setTimeout(() => {
            const buttons = document.getElementsByClassName('ac-pushButton');
            for (let i = 0; i < buttons.length; i++) {
                if (
                    buttons[i].getAttribute('aria-label') === userInfo.gender &&
                    !buttons[i].classList.value.includes('disabled')
                ) {
                    const selectedElement: HTMLElement = buttons[
                        i
                    ] as HTMLElement;
                    if (selectedElement) {
                        selectedElement.click();
                        selectButton(selectedElement);
                    }
                }
            }
        }, 1000);
    }
};

/*
A chain of middleware that allows the developer to modify the store actions.
The middleware signature is the following:
createStore: ({}, ({ dispatch }) => next => action => next(cardAction)
*/
const initStore = (
    session: Session,
    featureFlags: AmpFlagSet,
    skipGreeting: boolean,
    setCustomTextInput: (customTextInput: CustomTextInput) => void,
    setInputMode: (mode: InputMode) => void,
    setInactive: React.Dispatch<React.SetStateAction<boolean>>,
    showGlobalInformationAlert: (
        type: GlobalInformationAlertType,
        value: GlobalInformationAlertValue
    ) => void,
    setDisableTextSend: (mode: boolean, reason?: DisableReason) => void,
    config: Config,
    healthSystemToken?: string | null,
    initLoaderValues?: InitLoaderValues | null,
    location?: GeoLocation
): any => {
    // extract the data from the JWT
    const user = {
        id: session.userId,
        name: session.userName,
    };

    fetchPcpLinkedInfoForAnalytics(
        healthSystemToken,
        config.userInfoForAnalytics
    );

    const sendUserLocation = async (dispatch: Dispatch): Promise<void> => {
        dispatch({
            type: 'DIRECT_LINE/POST_ACTIVITY',
            payload: {
                type: 'message',
                text: JSON.stringify(location),
                from: user,
                correlationId: getCorrelationId(),
            },
        });
    };

    let activeIntervalId: NodeJS.Timeout | undefined = undefined;

    const startActivityTimer = () => {
        lastActive = new Date().getTime();
        activeIntervalId = setInterval(() => {
            const now = new Date().getTime();
            if (lastActive + INACTIVE_TIMEOUT_MS < now) {
                setInactive(true);
                stopActivityTimer();
                clearRehydrationData(config.tier);
            }
        }, 10000);
    };

    const stopActivityTimer = () => {
        if (activeIntervalId !== undefined) {
            clearInterval(activeIntervalId);
            activeIntervalId = undefined;
        }
    };

    startActivityTimer();

    liveChatService.eventsStream.listen((event) => {
        if (event instanceof OnLiveChatOpeningEvent) {
            stopActivityTimer();
        } else if (
            event instanceof OnLiveChatActiveEvent &&
            activeIntervalId === undefined
        ) {
            startActivityTimer();
        } else if (
            event instanceof OnLiveChatActiveEvent &&
            activeIntervalId !== undefined
        ) {
            resetLastActiveTime();
        } else if (
            event instanceof OnLiveChatClosedEvent &&
            activeIntervalId === undefined
        ) {
            startActivityTimer();
        } else if (
            event instanceof OnLiveChatClosedInTabEvent &&
            activeIntervalId === undefined
        ) {
            startActivityTimer();
        }
    });

    initTealium();

    store = createStore(
        {},
        ({ dispatch }: { dispatch: Dispatch }) =>
            (next: Dispatch) =>
            (action: Action): any => {
                const tenantName = getTenantName();
                const correlationId = getCorrelationId();
                switch (action.type) {
                    case 'DIRECT_LINE/CONNECT_FULFILLED': {
                        if (isGenesysResume()) {
                            return;
                        }

                        if (
                            !shouldReconnect(featureFlags, session) ||
                            getCurrentWatermark(featureFlags) === undefined
                        ) {
                            setTokenFromGet(
                                featureFlags,
                                session,
                                getSessionToken(featureFlags),
                                config.tier
                            );
                            setCurrentWatermark(featureFlags, '0', config.tier);
                            const conversationId =
                                action.payload.directLine.conversationId;
                            const initArgs = {
                                app_version: getAppVersion(),
                                brand: getBrand(config.brandParentUrl),
                                browser: isMobileBrowser()
                                    ? 'Mobile-Web'
                                    : 'Desktop',
                                clientType: navigator.userAgent,
                                conversationId,
                                correlationId: correlationId,
                                customScheme: getCustomScheme(),
                                directlineConversationId: conversationId,
                                directLineDomain:
                                    action.payload.directLine.domain,
                                entrypoint: getUrlParam('entrypoint'),
                                amplitudeUserId: getAnalyticsUserId(
                                    initLoaderValues,
                                    healthSystemToken
                                ),
                                featureFlags: featureFlags || {},
                                isMobile: isMobile(),
                                isMobileBrowser: isMobileBrowser(),
                                lat: location?.lat,
                                long: location?.long,
                                messageAssistSendRecipientName:
                                    getRecipientName(),
                                messageAssistSendRecipientId: getRecipientId(),
                                mychartEnabledUser: isMychartEnabledUser(),
                                openMenuSelection:
                                    getUrlParam(
                                        'entrypointUtterance'
                                    )?.replaceAll('+', ' ') || 'Grace CTA',
                                originHost: getOriginHost(),
                                sessionId: getSessionId(),
                                siteReferral:
                                    getUrlParam('referrer').split('?')[0],
                                skipGreeting,
                                state: getUrlParam('state'),
                                tenantName,
                                token: healthSystemToken,
                                unified: isUnified(),
                                userId: user.id,
                                viewport:
                                    window.screen.width +
                                    ' x ' +
                                    window.screen.height,
                                zipcode: getUrlParam('zipcode'),
                            };

                            // GRAC-3073 was a bug where sometimes the feature flag map is empty which throws an error in rasa and stops the conversation
                            // If we don't have feature flags, don't start the conversation and the user will see the standard error messages to try again
                            if (Object.keys(featureFlags).length === 0) {
                                logEvent({
                                    name: 'FeatureFlagEmpty',
                                    properties: {
                                        conversation_id: getConversationId(),
                                        correlation_id: getCorrelationId(),
                                    },
                                });

                                if (getTenantName() == TENANTS.MESSAGE_ASSIST) {
                                    postMessageAssistMessage(
                                        MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.ON_GRACE_LOAD_FAIL
                                    );
                                }

                                return;
                            }

                            dispatch({
                                type: 'WEB_CHAT/SEND_MESSAGE',
                                payload: {
                                    channelData: { postBack: true },
                                    text: `/navigation.statement-greet${JSON.stringify(
                                        initArgs
                                    )}`,
                                    textFormat: 'plain',
                                    correlationId,
                                    timestamp: new Date().toISOString(),
                                },
                            });
                            logEvent({
                                name: 'Successfully sent greet message to bot',
                            });
                            clearTextInputEnabled(featureFlags, config.tier);
                        } else {
                            setInputMode(getTextInputEnabled(featureFlags));
                            let page_url = getUrlParam('referrer');
                            if (page_url) {
                                page_url = page_url.split('?')[0];
                            }
                            logAnalyticsEvent('Conversation Persisted', {
                                bot_userid: session.userId,
                                conversation_id: getConversationIdFromToken(
                                    session.connectorToken
                                ),
                                logged_in: !!healthSystemToken,
                                page_url,
                                browser: isMobileBrowser()
                                    ? 'Mobile-Web'
                                    : 'Desktop',
                                chatbot_tenant: 'grace-' + tenantName,
                                open_menu_selection:
                                    getUrlParam(
                                        'entrypointUtterance'
                                    )?.replaceAll('+', ' ') || 'Grace CTA',
                                site_referral: page_url,
                                viewport:
                                    window.screen.width +
                                    ' x ' +
                                    window.screen.height,
                            });
                        }
                        break;
                    }
                    case 'DIRECT_LINE/INCOMING_ACTIVITY': {
                        // Activities documented here:
                        // https://docs.microsoft.com/en-us/azure/bot-service/dotnet/bot-builder-dotnet-activities?view=azure-bot-service-3.0
                        const { activity } = action.payload as {
                            activity: Activity;
                        };

                        const watermark = activity.id.split('|')[1];
                        setWatermark(watermark);

                        if (activity.type === 'event') {
                            handleEventActivity({
                                activity,
                                watermark,
                                featureFlags,
                                correlationId,
                                dispatch,
                                config,
                                setInputMode,
                                setInactive,
                                sendUserLocation,
                                setUserInfo: (info) => {
                                    userInfo = info;
                                },
                                showGlobalInformationAlert,
                            });
                        } else if (activity.type === 'message') {
                            if (activity.text) {
                                activity.text = replaceTenantSpecificData(
                                    activity.text,
                                    tenantName
                                );
                                activity.text = replacePlaceholders(
                                    activity.text
                                );
                            }
                            if (
                                activity.from.role === 'user' &&
                                !ignoreMessageForDisableTextSend(activity)
                            ) {
                                setDisableTextSend(true, DisableReasons.OTHER);
                            } else if (activity.from.role === 'bot') {
                                setDisableTextSend(false, DisableReasons.OTHER);
                                if (autoResponse)
                                    sendAutoResponseBasedOnUserInfo(activity); // TODO: figure this out
                            }
                            // notify parent of chat activity
                            postChatActivity();

                            // handle entities
                            if (activity.entities) {
                                for (const entity of activity.entities) {
                                    switch (
                                        (
                                            entity as
                                                | AmplitudeEntity
                                                | CustomFeedbackEntity
                                                | InputModeEntity
                                                | WatermarkEntity
                                        ).name
                                    ) {
                                        case 'amplitudeEvent': {
                                            if (
                                                !isRehydratedMessage(
                                                    featureFlags,
                                                    watermark
                                                )
                                            ) {
                                                logAnalyticsEvent(
                                                    (entity as AmplitudeEntity)
                                                        .value.eventType,
                                                    (entity as AmplitudeEntity)
                                                        .value.eventProperties
                                                );
                                            }
                                            break;
                                        }
                                        case 'setInputMode': {
                                            if (
                                                !isRehydratedMessage(
                                                    featureFlags,
                                                    watermark
                                                )
                                            ) {
                                                setInputMode(
                                                    (entity as InputModeEntity)
                                                        .value
                                                );
                                                setTextInputEnabled(
                                                    featureFlags,
                                                    config.tier,
                                                    (entity as InputModeEntity)
                                                        .value
                                                );
                                            }

                                            break;
                                        }
                                        case 'customInputType': {
                                            setCustomTextInput(
                                                (entity as CustomFeedbackEntity)
                                                    .value
                                            );

                                            break;
                                        }
                                        case 'setWatermark': {
                                            setCheckpointWatermark(
                                                featureFlags,
                                                activity.id.split('|')[1],
                                                config.tier
                                            );
                                            break;
                                        }
                                    }
                                }
                            }

                            // Update links rendered by bot
                            setTimeout(() => {
                                const links = Array.from(
                                    document.getElementsByTagName('a')
                                );
                                const ecvLinks = links.filter(
                                    (link) =>
                                        link.href.includes(
                                            'scheduling.care.psjhealth.org'
                                        ) ||
                                        link.href.includes(
                                            'virtual.providence.org'
                                        ) ||
                                        link.href.includes(
                                            'virtual.swedish.org'
                                        )
                                );
                                for (const link of ecvLinks) {
                                    const protocol = link.href.split('://')[0];
                                    const restOfUrl = link.href.split('://')[1];
                                    const customScheme = getCustomScheme();
                                    if (
                                        customScheme.includes('hc') && // customScheme default is 'https' anyway
                                        !protocol.includes('hc') && // means already done
                                        restOfUrl.includes('virtual.') // '.' is important b/c the scheduling url includes 'virtual' too
                                    ) {
                                        link.href =
                                            customScheme + '://' + restOfUrl;
                                    }
                                }
                                addLinkClickEvents();
                            }, 1000);
                        }

                        const watermarkFromStorage =
                            getCurrentWatermark(featureFlags);
                        if (watermarkFromStorage) {
                            if (
                                parseInt(watermark) >
                                parseInt(watermarkFromStorage)
                            ) {
                                setCurrentWatermark(
                                    featureFlags,
                                    watermark,
                                    config.tier
                                );
                            }
                        } else {
                            setCurrentWatermark(
                                featureFlags,
                                watermark,
                                config.tier
                            );
                        }

                        break;
                    }
                    case 'DIRECT_LINE/POST_ACTIVITY': {
                        const { activity } = action.payload as {
                            activity: Activity;
                        };
                        if (
                            activity?.channelData?.postBack &&
                            activity?.value?.text
                        ) {
                            activity.text = activity.value.text;
                        }
                        if (healthSystemToken) {
                            activity.token = healthSystemToken;
                        }
                        activity.metadata = {
                            parentUrlPath: getUrlParam('parentUrlPath'),
                            originHost: getOriginHost(),
                        };
                        lastActive = new Date().getTime();
                        break;
                    }
                    case 'DIRECT_LINE/DISCONNECT':
                        break;
                }
                // reset cookie expiration on interaction with bot
                setRehydrationExpirations(
                    featureFlags,
                    REHYDRATION_COOKIE_EXP_MINUTES,
                    config.tier
                );
                return next(action);
            }
    );

    return store;
};

export function resetLastActiveTime(): void {
    lastActive = new Date().getTime();
}

export default initStore;
